const europeanDatePattern = /^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/;

function tryNormalizeTextDate(value: string): string | null {
	const match = value.match(europeanDatePattern);
	if (!match) return null;

	const day = Number(match[1]);
	const month = Number(match[2]);
	const year = Number(match[3]);

	if (month < 1 || month > 12 || day < 1 || day > 31) return null;

	const date = new Date(year, month - 1, day);
	if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
		return null;
	}

	const yyyy = String(year);
	const mm = String(month).padStart(2, '0');
	const dd = String(day).padStart(2, '0');
	return `${yyyy}-${mm}-${dd}`;
}

export const parseXls = async (file: File) => {
	// Lazy load xlsx to avoid bundle bloat
	const XLSX = await import('xlsx');
	return new Promise<{ columns: string[]; rows: Record<string, any>[] }>((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = event => {
			try {
				const data = new Uint8Array(event.target?.result as ArrayBuffer);
				const workbook = XLSX.read(data, { type: 'array', cellDates: true });
				const firstSheetName = workbook.SheetNames[0];
				const worksheet = workbook.Sheets[firstSheetName];

				// Normalize date cells to ISO strings so locale-formatted dates
				// (e.g. DD.MM.YYYY in Norwegian Excel) don't fail validation downstream
				for (const cellRef of Object.keys(worksheet)) {
					if (cellRef.startsWith('!')) continue;
					const cell = worksheet[cellRef];
					if (cell.t === 'd' && cell.v instanceof Date) {
						const d = cell.v;
						const yyyy = d.getFullYear();
						const mm = String(d.getMonth() + 1).padStart(2, '0');
						const dd = String(d.getDate()).padStart(2, '0');
						cell.t = 's';
						cell.v = `${yyyy}-${mm}-${dd}`;
						cell.w = cell.v;
					} else if (cell.t === 's' && typeof cell.v === 'string') {
						const normalized = tryNormalizeTextDate(cell.v.trim());
						if (normalized) {
							cell.v = normalized;
							cell.w = normalized;
						}
					}
				}

				const json = XLSX.utils.sheet_to_json(worksheet, {
					header: 1,
					raw: false,
					defval: ''
				}) as any[][];

				if (json.length === 0) return resolve({ columns: [], rows: [] });

				const allColumns = json[0].map((col: any) => String(col));

				// Find indices of non-empty columns (columns with a non-empty header)
				const nonEmptyColumnIndices = allColumns.reduce<number[]>((acc, col, idx) => {
					if (col.trim() !== '') acc.push(idx);
					return acc;
				}, []);

				const columns = nonEmptyColumnIndices.map(idx => allColumns[idx]);

				const rows = json
					.slice(1)
					.filter(rowArr =>
						// Filter out rows where all values are empty
						nonEmptyColumnIndices.some(idx => {
							const val = rowArr[idx];
							return val !== undefined && val !== null && String(val).trim() !== '';
						})
					)
					.map(rowArr => {
						const row: Record<string, any> = {};
						nonEmptyColumnIndices.forEach((colIdx, i) => {
							row[columns[i]] = rowArr[colIdx] ?? '';
						});
						return row;
					});

				resolve({ columns, rows });
			} catch (error) {
				reject(error);
			}
		};
		reader.onerror = reject;
		reader.readAsArrayBuffer(file);
	});
};
